// @ts-nocheck due to type check issue for react-native-keychain
import * as Keychain from 'react-native-keychain';
import { fetchAuthSession } from 'aws-amplify/auth';

interface TokenInfo {
  token: string;
  expiresAt: number;
}

const parseJwt = (token: string) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('[TokenService] Error parsing JWT:', error);
    return null;
  }
};

const storeTokenSecurely = async (token: string, expiresAt?: number) => {
  try {
    // If expiresAt is not provided, try to get it from the token
    let tokenExpiry = expiresAt;
    if (!tokenExpiry) {
      const payload = parseJwt(token);
      tokenExpiry = payload?.exp
        ? payload.exp * 1000
        : Date.now() + 60 * 60 * 1000;
    }

    const tokenData: TokenInfo = {
      token,
      expiresAt: tokenExpiry,
    };

    await Keychain.setGenericPassword('authToken', JSON.stringify(tokenData), {
      service: 'authToken',
    });
  } catch (error) {
    console.error('Error storing the token securely', error);
  }
};

const getTokenSecurely = async (): Promise<string | null> => {
  try {
    const credentials = await Keychain.getGenericPassword({
      service: 'authToken',
    });

    if (credentials) {
      const tokenData: TokenInfo = JSON.parse(credentials.password);

      // Check if token is expired or about to expire
      const now = Date.now();
      if (tokenData.expiresAt < now) {
        console.log('[TokenService] Token expired ');
      }

      return tokenData.token;
    }
    return null;
  } catch (error) {
    console.error('Error retrieving the token securely', error);
    return null;
  }
};

// Keep track of the current refresh operation
let refreshTokenPromise: Promise<string | null> | null = null;

const refreshToken = async (): Promise<string | null> => {
  try {
    // If there's already a refresh in progress, return the same promise
    if (refreshTokenPromise) {
      console.log(
        '[TokenService] Refresh already in progress, reusing promise',
      );
      return refreshTokenPromise;
    }

    // Create new refresh promise
    console.log('[TokenService] Starting new token refresh...');
    refreshTokenPromise = (async () => {
      try {
        const { tokens } = await fetchAuthSession();
        const newToken = tokens?.idToken?.toString();

        if (newToken) {
          // Get expiration from the token itself
          const payload = parseJwt(newToken);
          if (!payload?.exp) {
            throw new Error('Invalid token format - no expiration found');
          }

          await storeTokenSecurely(newToken, payload.exp * 1000);
          console.log('[TokenService] Token refreshed successfully');
          return newToken;
        }

        throw new Error('Failed to get new token from fetchAuthSession');
      } finally {
        // Clear the promise so future calls can start a new refresh
        refreshTokenPromise = null;
      }
    })();

    return refreshTokenPromise;
  } catch (error) {
    console.error('[TokenService] Token refresh error:', error);
    // Clear the promise on error as well
    refreshTokenPromise = null;
    throw error;
  }
};

const clearToken = async () => {
  try {
    await Keychain.resetGenericPassword({ service: 'authToken' });
    console.log('[TokenService] Token cleared successfully');
  } catch (error) {
    console.error('[TokenService] Error clearing token:', error);
  }
};

const isTokenValid = async (): Promise<boolean> => {
  try {
    const token = await getTokenSecurely();
    return !!token;
  } catch (error) {
    console.error('[TokenService] Error checking token validity:', error);
    return false;
  }
};

export {
  storeTokenSecurely,
  getTokenSecurely,
  refreshToken,
  clearToken,
  isTokenValid,
};
